/**
 * layoutAutoFit — the camera target for "switched to a layout tab, fit the
 * whole sheet on screen" (AutoCAD parity: activating a layout shows the full
 * A4/A3/… page without the user having to run ZOOM EXTENTS).
 *
 * Extracted from the `Scene3DStage.tsx` effect that used to inline it so the
 * target is a pure, directly unit-testable function — `Scene3DStage.tsx` is
 * an orchestrator the jsdom suite cannot mount (R3F + WebGL), so inline
 * camera math there is untestable by construction.
 *
 * # Coordinate frame — the whole point of this module
 *
 * The sheet centre is derived from the sheet origin in the renderer's
 * OFFSET-world frame (`coordinateOrigin.applyOffsetToPaperOrigin`), NOT from
 * the raw `LayoutPageSetup.origin`. Every geometry adapter subtracts the
 * locked session origin `O` from every coordinate, paper-space entities
 * included, so a camera centred on the raw sheet centre looks at a spot `O`
 * away from the title block, the border and every other paper-space entity —
 * a blank white page with the content a million units off-screen on any
 * survey-scale drawing (measured: layout «ФП1», origin (1e6, 1e6) → 367710
 * white pixels, 0 ink).
 *
 * The conversion is performed INSIDE this function, from the `pageSetup`
 * itself, precisely so no caller can hand it the wrong frame.
 */

import type { LayoutPageSetup } from "@dxf-ai-editor/drawing-model";

import type { CameraStateSnapshot } from "../cameraApi";
import { applyOffsetToPaperOrigin } from "../coordinateOrigin";

/**
 * Fraction of the canvas the sheet fills, so the page has a visible margin
 * instead of touching the canvas edges. Carried over verbatim from the
 * `Scene3DStage.tsx` effect this was extracted from.
 */
export const LAYOUT_FIT_PADDING = 0.85;

/** Canvas size in CSS pixels (`Scene3DStage`'s `viewportSizeRef.current`). */
export interface LayoutFitViewportSize {
  width: number;
  height: number;
}

/**
 * Camera target that fits `pageSetup`'s sheet into `viewport`.
 *
 * Returns `null` when there is nothing sane to fit — a canvas that has not
 * been measured yet (width/height ≤ 0), or a degenerate sheet. A degenerate
 * sheet is unreachable through `Scene3DStage`'s own `paperPageSetup` memo
 * (`extractPageSetup` rejects a non-positive sheet and the memo falls back to
 * `defaultPageSetup()`), but returning `null` rather than an `Infinity` zoom
 * keeps this function total.
 */
export function computeLayoutFitCamera(
  pageSetup: LayoutPageSetup,
  viewport: LayoutFitViewportSize,
): CameraStateSnapshot | null {
  const { widthMm: w, heightMm: h } = pageSetup.sheetSize;
  if (w <= 0 || h <= 0) return null;
  if (viewport.width <= 0 || viewport.height <= 0) return null;

  // OFFSET-world sheet origin — the same value the sheet mesh, the VP frames
  // and the pointer hit-tests are placed from.
  const origin = applyOffsetToPaperOrigin(pageSetup.origin);

  const zoomX = viewport.width / w;
  const zoomY = viewport.height / h;
  return {
    centerX: origin.x + w / 2,
    centerY: origin.y + h / 2,
    zoom: Math.min(zoomX, zoomY) * LAYOUT_FIT_PADDING,
  };
}
